import React, { useState, useMemo } from 'react';
import { Sparkles, Clock, Flame, BookOpen } from 'lucide-react';
import { getTimeAgo } from '../lib/dateUtils';

const calculateReadTime = (content) => {
  if (!content) return '2 min read';
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};

const SidebarArticleCard = ({ article, onClick }) => {
  if (!article) return null;
  const title = article.title_en || article.title || article.title_rw || 'Untitled Article';
  const imgUrl = article.image_url || article.image || article.thumbnail;
  const source = article.source || article.author || 'YEGO';
  const timeAgo = getTimeAgo(article.published_at || article.created_at || article.date);
  const readTime = article.read_time || calculateReadTime(article.content || article.description);

  return (
    <div
      onClick={() => onClick && onClick(article)}
      className="group flex gap-3 p-2 rounded-xl hover:bg-gray-100/80 dark:hover:bg-gray-800/60 transition-all cursor-pointer select-none border border-transparent hover:border-gray-200/50 dark:hover:border-gray-700/50"
    >
      {/* Thumbnail (YouTube style aspect ratio with rounded corners) */}
      <div className="w-28 sm:w-32 xl:w-36 h-18 sm:h-20 xl:h-22 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 relative shadow-sm border border-gray-200/40 dark:border-gray-800">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/10 to-emerald-600/20 text-emerald-600 dark:text-emerald-400 font-bold text-lg">
            {source.charAt(0) || 'Y'}
          </div>
        )}
        {/* Small Read Time Badge (like YouTube video length) */}
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/75 backdrop-blur-[2px] text-[9.5px] font-semibold text-white tracking-tight">
          {readTime.replace(' min read', 'm').replace(' read', '')}
        </div>
      </div>

      {/* Details (Right Side) */}
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        <div>
          {article.category && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 line-clamp-1 mb-0.5">
              {article.category}
            </span>
          )}
          <h4 className="text-xs xl:text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors line-clamp-2 leading-snug">
            {title}
          </h4>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 mt-1">
          <span className="truncate max-w-[110px] font-medium">{source}</span>
          <span className="text-gray-300 dark:text-gray-600">•</span>
          <span className="flex-shrink-0">{timeAgo}</span>
        </div>
      </div>
    </div>
  );
};

const SuggestedArticlesSidebar = ({
  currentArticleId,
  relatedArticles = [],
  recentArticles = [],
  trendingArticles = [],
  onArticleClick
}) => {
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'related', 'trending', 'recent'

  // Combine and deduplicate articles for top feed (strictly 10)
  const filteredArticles = useMemo(() => {
    const seen = new Set();
    if (currentArticleId) {
      seen.add(String(currentArticleId));
    }

    let sourceList = [];
    if (activeFilter === 'related') {
      sourceList = relatedArticles;
    } else if (activeFilter === 'trending') {
      sourceList = trendingArticles.length > 0 ? trendingArticles : relatedArticles;
    } else if (activeFilter === 'recent') {
      sourceList = recentArticles.length > 0 ? recentArticles : relatedArticles;
    } else {
      // 'all': Interleave related, trending, and recent for a rich mix
      const maxLen = Math.max(relatedArticles.length, trendingArticles.length, recentArticles.length);
      for (let i = 0; i < maxLen; i++) {
        if (relatedArticles[i]) sourceList.push(relatedArticles[i]);
        if (trendingArticles[i]) sourceList.push(trendingArticles[i]);
        if (recentArticles[i]) sourceList.push(recentArticles[i]);
      }
    }

    const unique = [];
    for (const art of sourceList) {
      if (!art) continue;
      const artId = String(art.id || art._id || art.slug || art.title);
      if (!seen.has(artId)) {
        seen.add(artId);
        unique.push(art);
      }
    }

    // Strictly 10 vertical feed articles
    return unique.slice(0, 10);
  }, [currentArticleId, relatedArticles, recentArticles, trendingArticles, activeFilter]);

  // Recent Stories feed (excluding current article)
  const recentFeed = useMemo(() => {
    const currentId = String(currentArticleId || '');
    return recentArticles
      .filter(art => art && String(art.id || art._id || art.slug || art.title) !== currentId)
      .slice(0, 5);
  }, [recentArticles, currentArticleId]);

  // More to Read feed (excluding current article)
  const moreToReadFeed = useMemo(() => {
    const currentId = String(currentArticleId || '');
    return relatedArticles
      .filter(art => art && String(art.id || art._id || art.slug || art.title) !== currentId)
      .slice(0, 5);
  }, [relatedArticles, currentArticleId]);

  return (
    <aside className="space-y-6">
      {/* 1. We think you may like - Top Section */}
      <div className="space-y-4">
        {/* Header with Filter Chips (YouTube style) */}
        <div className="space-y-2.5 pb-2 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5 text-emerald-500 fill-emerald-500/20" />
              <span>We think you may like</span>
            </div>
            <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-200/50 dark:border-emerald-800/50">
              {filteredArticles.length} Stories
            </span>
          </div>

          {/* YouTube-style Filter Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
            <button
              type="button"
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeFilter === 'all'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('related')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                activeFilter === 'related'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              Related
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('trending')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1 ${
                activeFilter === 'trending'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Flame className="h-3 w-3 text-amber-500" />
              Trending
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('recent')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1 ${
                activeFilter === 'recent'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Clock className="h-3 w-3 text-blue-500" />
              Recent
            </button>
          </div>
        </div>

        {/* 10 Vertical Articles List (6 visible, remaining 4 scrollable) */}
        <div className="space-y-2.5 max-h-[580px] xl:max-h-[620px] overflow-y-auto pr-1 scrollbar-thin overscroll-contain">
          {filteredArticles.length === 0 ? (
            <div className="p-4 text-center rounded-xl bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500">
              No suggested articles in this section
            </div>
          ) : (
            filteredArticles.map((art, idx) => (
              <SidebarArticleCard
                key={art.id || `suggested-${idx}`}
                article={art}
                onClick={onArticleClick}
              />
            ))
          )}
        </div>
      </div>

      {/* 2. Recent Stories Section (Below the 10 articles) */}
      {recentFeed.length > 0 && (
        <div className="pt-5 border-t border-gray-100 dark:border-gray-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              <Clock className="h-3.5 w-3.5 text-blue-500" />
              <span>Recent Stories</span>
            </div>
            <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
              {recentFeed.length} Stories
            </span>
          </div>
          <div className="space-y-2.5">
            {recentFeed.map((art, idx) => (
              <SidebarArticleCard
                key={art.id || `recent-${idx}`}
                article={art}
                onClick={onArticleClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* 3. More to Read Section (Below Recent Stories) */}
      {moreToReadFeed.length > 0 && (
        <div className="pt-5 border-t border-gray-100 dark:border-gray-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              <BookOpen className="h-3.5 w-3.5 text-emerald-500" />
              <span>More to Read</span>
            </div>
            <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
              {moreToReadFeed.length} Stories
            </span>
          </div>
          <div className="space-y-2.5">
            {moreToReadFeed.map((art, idx) => (
              <SidebarArticleCard
                key={art.id || `more-${idx}`}
                article={art}
                onClick={onArticleClick}
              />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default SuggestedArticlesSidebar;
